import React from 'react';
import { motion } from 'framer-motion';
import Footer from '../../components/Footer';
import PG from "../../assets/courses/PG.gif";
import { Link } from 'react-router-dom';
import { GraduationCap, CheckCircle } from 'lucide-react';
import {
    Computer as ComputerIcon,
    Engineering as EngineeringIcon,
    Business as BusinessIcon,
    Science as ScienceIcon,
    Hotel as HotelIcon,
    LocalPharmacy as PharmacyIcon,
    Agriculture as AgricultureIcon,
    MedicalServices as MedicalIcon,
    Palette as ArtIcon,
    Gavel as LawIcon
} from '@mui/icons-material';

const pgSchools = [
    {
        name: 'School of Computer Engineering and Applications',
        icon: ComputerIcon,
        courses: [
            {
                name: 'M.Tech. Computer Science and Engineering',
                description: 'Advanced study in computer systems, algorithms, and software engineering principles.',
                duration: '2 Years',
                eligibility: 'B.Tech in CSE/IT with minimum 55% marks',
                features: [
                    'Advanced Algorithms',
                    'Distributed Systems',
                    'Cloud Computing',
                    'Research Methodology',
                    'Thesis Work'
                ],
                careerOpportunities: [
                    'Research Scientist',
                    'Software Architect',
                    'Technical Lead',
                    'System Designer',
                    'Professor'
                ]
            },
            {
                name: 'M.Tech. AIML',
                description: 'Specialized program in artificial intelligence and machine learning technologies.',
                duration: '2 Years',
                eligibility: 'B.Tech in CSE/IT/ECE or M.Sc in Computer Science',
                features: [
                    'Deep Learning',
                    'Neural Networks',
                    'Computer Vision',
                    'Natural Language Processing',
                    'AI Ethics'
                ],
                careerOpportunities: [
                    'AI Engineer',
                    'Machine Learning Specialist',
                    'Data Scientist',
                    'AI Researcher',
                    'Robotics Engineer'
                ]
            },
            {
                name: 'M.Tech. Data Science',
                description: 'Comprehensive study of data analysis, mining, and visualization techniques.',
                duration: '2 Years',
                eligibility: 'B.Tech in CSE/IT or M.Sc in Mathematics/Statistics',
                features: [
                    'Big Data Analytics',
                    'Machine Learning',
                    'Statistical Modeling',
                    'Data Visualization',
                    'Research Project'
                ],
                careerOpportunities: [
                    'Data Scientist',
                    'Data Analyst',
                    'Business Intelligence Specialist',
                    'Data Engineer',
                    'Research Analyst'
                ]
            },
            {
                name: 'M.Tech. Cyber Security',
                description: 'Advanced program focusing on network security and information protection.',
                duration: '2 Years',
                eligibility: 'B.Tech in CSE/IT/ECE or M.Sc in Computer Science',
                features: [
                    'Cryptography',
                    'Network Security',
                    'Ethical Hacking',
                    'Cyber Laws',
                    'Security Audit'
                ],
                careerOpportunities: [
                    'Cyber Security Analyst',
                    'Information Security Manager',
                    'Security Architect',
                    'Penetration Tester',
                    'Security Consultant'
                ]
            },
            {
                name: 'MCA',
                description: 'Master of Computer Applications focusing on advanced software development.',
                duration: '2 Years',
                eligibility: 'BCA or B.Sc (Computer Science) with Mathematics',
                features: [
                    'Advanced Programming',
                    'Database Systems',
                    'Web Technologies',
                    'Software Engineering',
                    'Project Work'
                ],
                careerOpportunities: [
                    'Software Developer',
                    'System Analyst',
                    'Database Administrator',
                    'IT Consultant',
                    'Project Manager'
                ]
            },
            {
                name: 'MCA AIML',
                description: 'Specialized MCA program with focus on artificial intelligence applications.',
                duration: '2 Years',
                eligibility: 'BCA or B.Sc (Computer Science) with Mathematics',
                features: [
                    'AI Programming',
                    'Machine Learning',
                    'Intelligent Systems',
                    'Data Mining',
                    'Capstone Project'
                ],
                careerOpportunities: [
                    'AI Application Developer',
                    'Machine Learning Engineer',
                    'Data Analyst',
                    'AI Solutions Architect',
                    'Research Associate'
                ]
            },
            {
                name: 'MCA Data Science',
                description: 'MCA specialization in data science and analytics.',
                duration: '2 Years',
                eligibility: 'BCA or B.Sc (Computer Science) with Mathematics',
                features: [
                    'Data Structures',
                    'Statistical Analysis',
                    'Predictive Modeling',
                    'Big Data Technologies',
                    'Industry Project'
                ],
                careerOpportunities: [
                    'Data Analyst',
                    'Business Analyst',
                    'Data Engineer',
                    'Database Manager',
                    'Analytics Consultant'
                ]
            },
            {
                name: 'MCA Cyber Security',
                description: 'MCA program specializing in cyber security principles.',
                duration: '2 Years',
                eligibility: 'BCA or B.Sc (Computer Science) with Mathematics',
                features: [
                    'Information Security',
                    'Network Defense',
                    'Digital Forensics',
                    'Security Protocols',
                    'Practical Labs'
                ],
                careerOpportunities: [
                    'Security Analyst',
                    'Network Administrator',
                    'Security Consultant',
                    'Forensic Expert',
                    'IT Auditor'
                ]
            },
            {
                name: 'M.Sc. Computer Science',
                description: 'Advanced theoretical and practical study of computer science.',
                duration: '2 Years',
                eligibility: 'B.Sc in Computer Science or related field',
                features: [
                    'Advanced Programming',
                    'Computer Networks',
                    'Algorithm Design',
                    'Research Methods',
                    'Dissertation'
                ],
                careerOpportunities: [
                    'Software Developer',
                    'Systems Analyst',
                    'Research Scientist',
                    'Technical Writer',
                    'Academician'
                ]
            }
        ]
    },
    {
        name: 'School of Hotel Management and Tourism',
        icon: HotelIcon,
        courses: [
            {
                name: 'Master of Hotel Management (MHM)',
                description: 'Advanced program in hospitality leadership and management.',
                duration: '2 Years',
                eligibility: 'Bachelor in Hotel Management or related field',
                features: [
                    'Strategic Management',
                    'Revenue Optimization',
                    'Quality Standards',
                    'Global Hospitality',
                    'Research Project'
                ],
                careerOpportunities: [
                    'Hotel General Manager',
                    'Hospitality Consultant',
                    'Corporate Manager',
                    'Resort Director',
                    'Hospitality Educator'
                ]
            }
        ]
    },
    {
        name: 'School of Engineering',
        icon: EngineeringIcon,
        courses: [
            {
                name: 'M.Tech. Mechanical Engineering',
                description: 'Advanced study in mechanical systems and thermal sciences.',
                duration: '2 Years',
                eligibility: 'B.Tech in Mechanical Engineering',
                features: [
                    'Advanced Thermodynamics',
                    'Finite Element Analysis',
                    'Robotics',
                    'Mechatronics',
                    'Research Thesis'
                ],
                careerOpportunities: [
                    'Mechanical Design Engineer',
                    'R&D Engineer',
                    'Project Manager',
                    'Automotive Engineer',
                    'Academia'
                ]
            },
            {
                name: 'M.Tech. Structural Engineering',
                description: 'Specialized program in structural analysis and design.',
                duration: '2 Years',
                eligibility: 'B.Tech in Civil Engineering',
                features: [
                    'Structural Analysis',
                    'Earthquake Engineering',
                    'Concrete Technology',
                    'Steel Structures',
                    'Design Project'
                ],
                careerOpportunities: [
                    'Structural Engineer',
                    'Design Consultant',
                    'Construction Manager',
                    'Project Engineer',
                    'Research Specialist'
                ]
            }
        ]
    },
    {
        name: 'School of Commerce and Management',
        icon: BusinessIcon,
        courses: [
            {
                name: 'MBA Human Resources',
                description: 'Specialized program in human capital management and development.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Strategic HRM',
                    'Organizational Behavior',
                    'Talent Management',
                    'Industrial Relations',
                    'Corporate Internship'
                ],
                careerOpportunities: [
                    'HR Manager',
                    'Recruitment Specialist',
                    'Training Manager',
                    'Compensation Analyst',
                    'HR Consultant'
                ]
            },
            {
                name: 'MBA Marketing',
                description: 'Advanced study of marketing strategies and consumer behavior.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Consumer Behavior',
                    'Brand Management',
                    'Digital Marketing',
                    'Market Research',
                    'Live Projects'
                ],
                careerOpportunities: [
                    'Marketing Manager',
                    'Brand Strategist',
                    'Sales Manager',
                    'Market Analyst',
                    'Advertising Executive'
                ]
            },
            {
                name: 'MBA FinTech',
                description: 'Program focusing on financial technologies and digital banking.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Blockchain Technology',
                    'Digital Payments',
                    'Financial Analytics',
                    'Regulatory Aspects',
                    'Industry Projects'
                ],
                careerOpportunities: [
                    'FinTech Analyst',
                    'Digital Banking Specialist',
                    'Financial Consultant',
                    'Blockchain Developer',
                    'Risk Manager'
                ]
            },
            {
                name: 'MBA Digital Marketing',
                description: 'Specialization in digital marketing strategies and analytics.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Digital Analytics',
                    'Social Media Marketing',
                    'SEO/SEM',
                    'Content Strategy',
                    'E-commerce Marketing'
                ],
                careerOpportunities: [
                    'Digital Marketing Manager',
                    'SEO Specialist',
                    'Social Media Manager',
                    'Content Strategist',
                    'Digital Analyst'
                ]
            },
            {
                name: 'MBA Travel & Tourism',
                description: 'Program focusing on tourism management and hospitality services.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Tourism Economics',
                    'Destination Management',
                    'Hospitality Marketing',
                    'Sustainable Tourism',
                    'Industry Internship'
                ],
                careerOpportunities: [
                    'Tourism Manager',
                    'Travel Consultant',
                    'Event Planner',
                    'Resort Manager',
                    'Tour Operator'
                ]
            },
            {
                name: 'MBA Logistic Management',
                description: 'Specialization in supply chain and logistics management.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Supply Chain Management',
                    'Inventory Control',
                    'Transportation Systems',
                    'Warehouse Management',
                    'Case Studies'
                ],
                careerOpportunities: [
                    'Logistics Manager',
                    'Supply Chain Analyst',
                    'Operations Manager',
                    'Procurement Specialist',
                    'Distribution Manager'
                ]
            }
        ]
    },
    {
        name: 'School of Life and Applied Science',
        icon: ScienceIcon,
        courses: [
            {
                name: 'M.Sc. Physics',
                description: 'Advanced study of theoretical and experimental physics.',
                duration: '2 Years',
                eligibility: 'B.Sc in Physics or related field',
                features: [
                    'Quantum Mechanics',
                    'Electrodynamics',
                    'Statistical Physics',
                    'Research Methodology',
                    'Laboratory Work'
                ],
                careerOpportunities: [
                    'Research Scientist',
                    'Physicist',
                    'Academician',
                    'Technical Consultant',
                    'Lab Technician'
                ]
            },
            {
                name: 'M.Sc. Chemistry',
                description: 'Advanced program in chemical sciences and research.',
                duration: '2 Years',
                eligibility: 'B.Sc in Chemistry or related field',
                features: [
                    'Advanced Organic Chemistry',
                    'Spectroscopy',
                    'Polymer Chemistry',
                    'Research Techniques',
                    'Project Work'
                ],
                careerOpportunities: [
                    'Chemical Analyst',
                    'Research Chemist',
                    'Quality Control Manager',
                    'Pharmaceutical Scientist',
                    'Professor'
                ]
            },
            {
                name: 'M.Sc. Maths',
                description: 'Advanced study of pure and applied mathematics.',
                duration: '2 Years',
                eligibility: 'B.Sc in Mathematics or related field',
                features: [
                    'Abstract Algebra',
                    'Real Analysis',
                    'Numerical Methods',
                    'Operations Research',
                    'Dissertation'
                ],
                careerOpportunities: [
                    'Mathematician',
                    'Data Analyst',
                    'Statistician',
                    'Actuary',
                    'Academic Researcher'
                ]
            },
            {
                name: 'M.Sc. Zoology',
                description: 'Advanced study of animal sciences and biodiversity.',
                duration: '2 Years',
                eligibility: 'B.Sc in Zoology or related field',
                features: [
                    'Animal Physiology',
                    'Wildlife Biology',
                    'Evolutionary Biology',
                    'Research Methods',
                    'Field Work'
                ],
                careerOpportunities: [
                    'Wildlife Biologist',
                    'Zoologist',
                    'Conservationist',
                    'Research Scientist',
                    'Museum Curator'
                ]
            },
            {
                name: 'M.Sc. Botany',
                description: 'Advanced program in plant sciences and biotechnology.',
                duration: '2 Years',
                eligibility: 'B.Sc in Botany or related field',
                features: [
                    'Plant Physiology',
                    'Plant Biotechnology',
                    'Ecology',
                    'Taxonomy',
                    'Research Project'
                ],
                careerOpportunities: [
                    'Plant Scientist',
                    'Biotechnologist',
                    'Ecologist',
                    'Horticulturist',
                    'Research Associate'
                ]
            },
            {
                name: 'M.Sc. Environmental Science',
                description: 'Study of environmental systems and sustainability.',
                duration: '2 Years',
                eligibility: 'B.Sc in any science discipline',
                features: [
                    'Environmental Chemistry',
                    'Ecology',
                    'Pollution Control',
                    'Environmental Policy',
                    'Field Studies'
                ],
                careerOpportunities: [
                    'Environmental Consultant',
                    'Conservation Officer',
                    'Pollution Control Officer',
                    'Research Scientist',
                    'Sustainability Manager'
                ]
            },
            {
                name: 'M.Sc. Forensic Science',
                description: 'Program in scientific investigation and crime analysis.',
                duration: '2 Years',
                eligibility: 'B.Sc in any science discipline',
                features: [
                    'Criminalistics',
                    'Toxicology',
                    'DNA Analysis',
                    'Crime Scene Investigation',
                    'Laboratory Training'
                ],
                careerOpportunities: [
                    'Forensic Scientist',
                    'Crime Scene Investigator',
                    'Toxicologist',
                    'DNA Analyst',
                    'Forensic Consultant'
                ]
            },
            {
                name: 'M.Sc. Microbiology',
                description: 'Advanced study of microorganisms and their applications.',
                duration: '2 Years',
                eligibility: 'B.Sc in Microbiology or related field',
                features: [
                    'Medical Microbiology',
                    'Industrial Microbiology',
                    'Immunology',
                    'Virology',
                    'Research Work'
                ],
                careerOpportunities: [
                    'Microbiologist',
                    'Quality Control Officer',
                    'Research Scientist',
                    'Pharmaceutical Analyst',
                    'Public Health Specialist'
                ]
            },
            {
                name: 'M.Sc. Biotechnology',
                description: 'Advanced program in biotechnological applications.',
                duration: '2 Years',
                eligibility: 'B.Sc in Biotechnology or related field',
                features: [
                    'Genetic Engineering',
                    'Bioinformatics',
                    'Industrial Biotechnology',
                    'Research Methodology',
                    'Lab Techniques'
                ],
                careerOpportunities: [
                    'Biotech Researcher',
                    'Quality Control Analyst',
                    'Pharmaceutical Scientist',
                    'Biotech Entrepreneur',
                    'Academic Researcher'
                ]
            }
        ]
    },
    {
        name: 'School of Pharmacy',
        icon: PharmacyIcon,
        courses: [
            {
                name: 'Pharm D (Doctor of Pharmacy)',
                description: 'Clinical pharmacy program with patient care focus.',
                duration: '6 Years',
                eligibility: '10+2 with Physics, Chemistry, Biology',
                features: [
                    'Clinical Pharmacy',
                    'Pharmacotherapeutics',
                    'Hospital Pharmacy',
                    'Community Pharmacy',
                    'Internship'
                ],
                careerOpportunities: [
                    'Clinical Pharmacist',
                    'Hospital Pharmacist',
                    'Drug Information Specialist',
                    'Pharmaceutical Researcher',
                    'Regulatory Affairs Officer'
                ]
            },
            {
                name: 'M. Pharma Pharmacology',
                description: 'Advanced study of drug action and therapeutic effects.',
                duration: '2 Years',
                eligibility: 'B.Pharma with minimum 55% marks',
                features: [
                    'Clinical Pharmacology',
                    'Toxicology',
                    'Drug Development',
                    'Research Methodology',
                    'Thesis Work'
                ],
                careerOpportunities: [
                    'Pharmacologist',
                    'Drug Safety Associate',
                    'Clinical Research Associate',
                    'Medical Writer',
                    'Regulatory Specialist'
                ]
            },
            {
                name: 'M. Pharma Pharmaceutics',
                description: 'Advanced study in drug formulation and delivery systems.',
                duration: '2 Years',
                eligibility: 'B.Pharma with minimum 55% marks',
                features: [
                    'Drug Design',
                    'Formulation Development',
                    'Biopharmaceutics',
                    'Regulatory Affairs',
                    'Research Project'
                ],
                careerOpportunities: [
                    'Formulation Scientist',
                    'Regulatory Affairs Officer',
                    'Quality Assurance Manager',
                    'Pharmaceutical Researcher',
                    'Production Manager'
                ]
            },
            {
                name: 'M. Pharma Pharmaceutical Chemistry',
                description: 'Program focusing on drug design and medicinal chemistry.',
                duration: '2 Years',
                eligibility: 'B.Pharma with minimum 55% marks',
                features: [
                    'Medicinal Chemistry',
                    'Drug Synthesis',
                    'Analytical Techniques',
                    'Molecular Modeling',
                    'Research Work'
                ],
                careerOpportunities: [
                    'Pharmaceutical Chemist',
                    'Analytical Chemist',
                    'Research Scientist',
                    'Quality Control Manager',
                    'Process Development Chemist'
                ]
            }
        ]
    },
    {
        name: 'School of Agriculture and Technology',
        icon: AgricultureIcon,
        courses: [
            {
                name: 'M.Sc. Agronomy',
                description: 'Advanced study of crop production and soil management.',
                duration: '2 Years',
                eligibility: 'B.Sc in Agriculture or related field',
                features: [
                    'Crop Physiology',
                    'Soil Fertility',
                    'Weed Management',
                    'Research Methods',
                    'Field Work'
                ],
                careerOpportunities: [
                    'Agronomist',
                    'Farm Manager',
                    'Crop Consultant',
                    'Research Scientist',
                    'Agricultural Officer'
                ]
            },
            {
                name: 'M.Sc. Genetics and Plant Breeding',
                description: 'Program in plant genetics and crop improvement techniques.',
                duration: '2 Years',
                eligibility: 'B.Sc in Agriculture or related field',
                features: [
                    'Plant Genetics',
                    'Breeding Methods',
                    'Biotechnology Applications',
                    'Statistical Genetics',
                    'Research Project'
                ],
                careerOpportunities: [
                    'Plant Breeder',
                    'Geneticist',
                    'Seed Technologist',
                    'Research Scientist',
                    'Biotechnologist'
                ]
            },
            {
                name: 'M.Sc. Horticulture',
                description: 'Advanced study of fruit, vegetable and ornamental plant cultivation.',
                duration: '2 Years',
                eligibility: 'B.Sc in Agriculture/Horticulture',
                features: [
                    'Pomology',
                    'Olericulture',
                    'Floriculture',
                    'Landscape Gardening',
                    'Practical Training'
                ],
                careerOpportunities: [
                    'Horticulturist',
                    'Nursery Manager',
                    'Landscape Designer',
                    'Research Officer',
                    'Extension Specialist'
                ]
            },
            {
                name: 'M.Sc. Food Technology',
                description: 'Program in food processing and preservation technologies.',
                duration: '2 Years',
                eligibility: 'B.Sc in Food Technology or related field',
                features: [
                    'Food Processing',
                    'Quality Control',
                    'Food Packaging',
                    'Food Microbiology',
                    'Industry Project'
                ],
                careerOpportunities: [
                    'Food Technologist',
                    'Quality Assurance Manager',
                    'Product Development Specialist',
                    'Food Safety Officer',
                    'Research Scientist'
                ]
            }
        ]
    },
    {
        name: 'School of Health Sciences',
        icon: MedicalIcon,
        courses: [
            {
                name: 'Master in Hospital Administration (MHA)',
                description: 'Program in healthcare management and hospital operations.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Healthcare Management',
                    'Hospital Operations',
                    'Health Economics',
                    'Quality Assurance',
                    'Internship'
                ],
                careerOpportunities: [
                    'Hospital Administrator',
                    'Healthcare Manager',
                    'Clinic Director',
                    'Health Services Manager',
                    'Medical Superintendent'
                ]
            },
            {
                name: 'Master in Public Health (MPH)',
                description: 'Program focusing on community health and epidemiology.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Epidemiology',
                    'Biostatistics',
                    'Health Policy',
                    'Environmental Health',
                    'Field Work'
                ],
                careerOpportunities: [
                    'Public Health Specialist',
                    'Epidemiologist',
                    'Health Educator',
                    'Community Health Manager',
                    'Policy Analyst'
                ]
            }
        ]
    },
    {
        name: 'School of Arts and Humanities',
        icon: ArtIcon,
        courses: [
            {
                name: 'Master in Social Work',
                description: 'Program in community development and social welfare.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Social Welfare',
                    'Community Development',
                    'Counseling',
                    'Research Methods',
                    'Field Work'
                ],
                careerOpportunities: [
                    'Social Worker',
                    'Community Organizer',
                    'Counselor',
                    'NGO Manager',
                    'Policy Analyst'
                ]
            },
            {
                name: 'MA Yoga',
                description: 'Advanced study of yogic sciences and therapy.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Yoga Philosophy',
                    'Therapeutic Yoga',
                    'Yoga Psychology',
                    'Research Methodology',
                    'Practical Training'
                ],
                careerOpportunities: [
                    'Yoga Therapist',
                    'Yoga Instructor',
                    'Wellness Consultant',
                    'Researcher',
                    'Rehabilitation Specialist'
                ]
            },
            {
                name: 'MA Maths',
                description: 'Advanced theoretical study of mathematical sciences.',
                duration: '2 Years',
                eligibility: 'BA/B.Sc in Mathematics',
                features: [
                    'Advanced Calculus',
                    'Algebra',
                    'Mathematical Modeling',
                    'Research Techniques',
                    'Dissertation'
                ],
                careerOpportunities: [
                    'Mathematician',
                    'Statistician',
                    'Actuary',
                    'Data Analyst',
                    'Academician'
                ]
            },
            {
                name: 'MA Clinical Psychology',
                description: 'Program in mental health assessment and therapy.',
                duration: '2 Years',
                eligibility: 'BA/B.Sc in Psychology',
                features: [
                    'Psychopathology',
                    'Psychological Assessment',
                    'Therapeutic Techniques',
                    'Research Methods',
                    'Clinical Internship'
                ],
                careerOpportunities: [
                    'Clinical Psychologist',
                    'Counselor',
                    'Mental Health Specialist',
                    'Rehabilitation Psychologist',
                    'Researcher'
                ]
            },
            {
                name: 'MA Psychology',
                description: 'Advanced study of human behavior and mental processes.',
                duration: '2 Years',
                eligibility: 'BA/B.Sc in Psychology',
                features: [
                    'Cognitive Psychology',
                    'Social Psychology',
                    'Developmental Psychology',
                    'Research Methodology',
                    'Project Work'
                ],
                careerOpportunities: [
                    'Psychologist',
                    'Human Resources Specialist',
                    'Counselor',
                    'Researcher',
                    'Academician'
                ]
            },
            {
                name: 'MA English',
                description: 'Advanced study of English literature and language.',
                duration: '2 Years',
                eligibility: 'BA in English or related field',
                features: [
                    'Literary Theory',
                    'World Literature',
                    'Linguistics',
                    'Creative Writing',
                    'Research Dissertation'
                ],
                careerOpportunities: [
                    'Content Writer',
                    'Editor',
                    'Professor',
                    'Public Relations Officer',
                    'Communications Specialist'
                ]
            },
            {
                name: 'MA Sociology',
                description: 'Study of social structures and human relationships.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Social Theory',
                    'Research Methods',
                    'Gender Studies',
                    'Urban Sociology',
                    'Field Work'
                ],
                careerOpportunities: [
                    'Social Researcher',
                    'Community Developer',
                    'Policy Analyst',
                    'NGO Worker',
                    'Academician'
                ]
            },
            {
                name: 'MA Political Science',
                description: 'Advanced study of political systems and governance.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Political Theory',
                    'International Relations',
                    'Public Administration',
                    'Research Methods',
                    'Dissertation'
                ],
                careerOpportunities: [
                    'Political Analyst',
                    'Policy Advisor',
                    'Diplomat',
                    'Researcher',
                    'Academician'
                ]
            },
            {
                name: 'MA Economics',
                description: 'Advanced study of economic theories and policies.',
                duration: '2 Years',
                eligibility: 'BA/B.Sc in Economics',
                features: [
                    'Microeconomics',
                    'Macroeconomics',
                    'Econometrics',
                    'Development Economics',
                    'Research Project'
                ],
                careerOpportunities: [
                    'Economist',
                    'Policy Analyst',
                    'Financial Consultant',
                    'Researcher',
                    'Academician'
                ]
            },
            {
                name: 'MA Hindi',
                description: 'Advanced study of Hindi literature and linguistics.',
                duration: '2 Years',
                eligibility: 'BA in Hindi or related field',
                features: [
                    'Hindi Literature',
                    'Linguistics',
                    'Creative Writing',
                    'Literary Criticism',
                    'Research Work'
                ],
                careerOpportunities: [
                    'Hindi Officer',
                    'Content Writer',
                    'Translator',
                    'Professor',
                    'Media Professional'
                ]
            },
            {
                name: 'MA Fine Arts',
                description: 'Advanced program in visual arts and creative expression.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in Fine Arts or related field',
                features: [
                    'Painting',
                    'Sculpture',
                    'Art History',
                    'Visual Communication',
                    'Studio Work'
                ],
                careerOpportunities: [
                    'Artist',
                    'Art Teacher',
                    'Gallery Curator',
                    'Art Director',
                    'Creative Consultant'
                ]
            },
            {
                name: 'MA Journalism & Mass Communication',
                description: 'Program in media studies and communication technologies.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Media Ethics',
                    'News Reporting',
                    'Digital Media',
                    'Public Relations',
                    'Internship'
                ],
                careerOpportunities: [
                    'Journalist',
                    'Media Planner',
                    'Public Relations Officer',
                    'Content Creator',
                    'Communication Specialist'
                ]
            },
            {
                name: 'MA Environmental Science',
                description: 'Interdisciplinary study of environmental issues and solutions.',
                duration: '2 Years',
                eligibility: 'Bachelor degree in any discipline',
                features: [
                    'Environmental Policy',
                    'Sustainable Development',
                    'Climate Change',
                    'Research Methods',
                    'Field Studies'
                ],
                careerOpportunities: [
                    'Environmental Consultant',
                    'Sustainability Manager',
                    'Policy Analyst',
                    'Conservation Officer',
                    'Researcher'
                ]
            }
        ]
    },
    {
        name: 'School of Law and Legal Studies',
        icon: LawIcon,
        courses: [
            {
                name: 'LLM',
                description: 'Advanced study of legal principles and specialized areas of law.',
                duration: '2 Years',
                eligibility: 'LLB degree',
                features: [
                    'Constitutional Law',
                    'Corporate Law',
                    'International Law',
                    'Legal Research',
                    'Dissertation'
                ],
                careerOpportunities: [
                    'Legal Consultant',
                    'Corporate Lawyer',
                    'Judge',
                    'Legal Advisor',
                    'Academician'
                ]
            }
        ]
    }
];

const Postgraduate = () => {
    return (
        <div className='w-full relative font-poppins bg-gradient-to-br from-green-50 to-white min-h-screen'>
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative pt-[100px] md:pt-[150px] text-center"
            >
                <h2 className="mb-4 text-4xl md:text-6xl tracking-tight font-bold text-[#0e4d38]">
                    Postgraduate Courses
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto px-4">
                    Advanced Specializations for Career Advancement
                </p>
            </motion.div>

            {/* Main Content */}
            <div className='w-11/12 mx-auto mb-[50px] relative z-10'>
                {/* Course Overview Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="flex flex-col gap-5 md:gap-[100px] md:flex-row justify-between items-center my-16"
                >
                    {/* PG Image */}
                    <motion.div
                        whileHover={{
                            scale: 1.05,
                            rotate: 2,
                            transition: { duration: 0.3 }
                        }}
                        className="relative rounded-2xl overflow-hidden shadow-2xl"
                    >
                        <img
                            src={PG}
                            className="w-[400px] h-[400px] max-h-[450px] object-contain md:object-cover"
                            alt="Postgraduate Courses"
                        />
                    </motion.div>

                    {/* Description Section */}
                    <div className="w-full md:w-1/2 space-y-6">
                        <h3 className="text-3xl font-bold text-[#0e4d38] mb-4">
                            Why Pursue PG at Our University?
                        </h3>
                        <div className="space-y-4 bg-white p-6 rounded-lg shadow-md">
                            {[
                                {
                                    icon: <CheckCircle className="text-green-500 mr-3" />,
                                    text: "Research-Oriented Curriculum"
                                },
                                {
                                    icon: <CheckCircle className="text-green-600 mr-3" />,
                                    text: "Industry-Experienced Faculty"
                                },
                                {
                                    icon: <CheckCircle className="text-green-700 mr-3" />,
                                    text: "Advanced Laboratory Facilities"
                                },
                                {
                                    icon: <CheckCircle className="text-green-800 mr-3" />,
                                    text: "Strong Industry-Academia Collaboration"
                                }
                            ].map((item, index) => (
                                <div key={index} className="flex items-center">
                                    {item.icon}
                                    <span className="text-gray-700">{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Schools Section */}
                <div className="space-y-16">
                    {pgSchools.map((school, schoolIndex) => (
                        <motion.div
                            key={schoolIndex}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 * schoolIndex }}
                            className="bg-white rounded-xl shadow-lg overflow-hidden"
                        >
                            {/* School Header */}
                            <div className="bg-gradient-to-r from-green-600 to-green-800 p-6">
                                <div className="flex items-center space-x-4">
                                    <school.icon sx={{ color: 'white', fontSize: 32 }} />
                                    <h3 className="text-white text-2xl font-bold">{school.name}</h3>
                                </div>
                            </div>

                            {/* Courses Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                                {school.courses.map((course, index) => (
                                    <motion.div
                                        key={index}
                                        whileHover={{ y: -5 }}
                                        className="border border-green-100 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <div className="p-6">
                                            <h4 className="text-lg font-semibold text-green-800 mb-2">{course.name}</h4>
                                            <p className="text-gray-600 text-sm mb-4">{course.description}</p>
                                            
                                            <div className="mb-4">
                                                <div className="text-sm text-green-700 font-medium mb-1">Duration: {course.duration}</div>
                                                <div className="text-sm text-green-700 font-medium">Eligibility: {course.eligibility}</div>
                                            </div>
                                            
                                            <ul className="text-sm text-gray-600 mb-4 space-y-1">
                                                {course.features.slice(0, 3).map((feature, idx) => (
                                                    <li key={idx} className="flex items-start">
                                                        <CheckCircle className="text-green-500 mt-0.5 mr-2 flex-shrink-0" size={16} />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="px-6 pb-4">
                                            <Link to="/programmes">
                                                <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded transition-colors">
                                                    View Details
                                                </button>
                                            </Link>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Postgraduate;